// Angular Core Modules/Libraries
import {
  Component,
  NgModule,
  VERSION,
  isDevMode,
  ModuleWithProviders,
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler
} from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FacebookModule } from 'ngx-facebook';

// Additional Library Modules
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { BrowserModule, HammerModule } from '@angular/platform-browser';



// App-level Routing configurations
import { Routing } from './app.routing';

// Import global shared module to access global features
import { CoreModule } from '@core/core.module';

// App Module components imports
import { AppComponent } from '@approot/app/app.component';
import { AppHeaderComponent } from '@approot/app-header/app-header.component';
import { AppFooterComponent } from '@approot/app-footer/app-footer.component';
import { LandingComponent } from '@approot/app-landing';
import { HomeComponent } from '@features/home';
import { EmergencyBannerComponent } from '@features/home/emergency-banner/emergency-banner.component';

// Import the shared components module
import { SharedComponentsModule } from '@shared/components';

import { MaterialsModule } from './material.module';

// Redux requirements
import { NgRedux, NgReduxModule, DevToolsExtension } from '@angular-redux/store';
import { rootReducer } from '@core/redux/wbstore';
import { IAppState, INITIAL_STATE } from '@core/redux/stores';
import { loadState, saveState } from '@core/redux/localstorage';

import { ResponsiveModule } from 'ngx-responsive';
import { HttpClientModule, HttpClient } from '@angular/common/http';

// Authenticate modal components
import * as AuthModals from '@features/authenticate/modals-index';

// Menu model components
import * as OrderModals from '@features/ordering/modals/modals.index';
import * as AccountModals from '@features/account/modals/modals.index';
import * as CustomizationModals from '@features/customization/modals/modals.index';
import { environment } from '@environments/environment';
import { StatsigModule, STATSIG_INIT_CONFIG } from '@statsig/angular-bindings';
import { StatsigClient } from '@statsig/js-client';
import { buildStatsigUser, FeatureFlagService, StatsigFeatureFlagService } from '@core/services/statsig';
import { StartOrderService } from '@features/ordering/services/start-order.service';
import { PickupTimeService } from '@features/ordering/services/pickup-time.service';
// Meal Option Service
import { MealOptionService } from '@features/customization/services/mealoption.service';

import { AddFavoriteNameService } from '@core/services/add-favorite-name.service';

// adding rx Observable operator libraries





import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { CanDeactivateGuard } from '@core/guards/can-deactivate.guard';



import { NgxStripeModule } from 'ngx-stripe';
import { SortablejsModule } from 'ngx-sortablejs';
import { ChildRecipeService } from '@features/ordering/services/child-recipe.service';
import { EditFavoriteService } from '@core/services/edit-favorite.service';
import { CustomizationService } from '@features/customization/services';
import { TaxCalculatorService } from '@ordering/services/tax-calculator.service';
import { MonitoringErrorHandler } from '@core/handlers/monitoring-error.handler';
import { LoggingService } from '@core/services/logging.service';
// import { DeviceDetectorModule } from 'ngx-device-detector';
import { FinalCheckoutService } from '@features/checkout/services';
import { SubHeaderComponent } from './app-root/sub-header/sub-header.component';
import { ApplyForSupportModalComponent } from '@features/community/apply-for-support-modal/apply-for-support-modal.component';
import { VisitTutorialComponent } from '@features/visit/visit-tutorial/visit-tutorial.component';
import { RecentOrdersComponent } from '@shared/components/recent-orders/recent-orders.component';
import { SigninCodeComponent } from './features/authenticate/signin-code/signin-code.component';
import { Auth0CallbackComponent } from '@features/authenticate/auth0-callback';

import {NgxMaskModule} from 'ngx-mask';
import { BannerNudgeComponent } from '@features/ordering/banner-nudge/banner-nudge.component';
import { AssumedLocationBannerComponent } from '@approot/assumed-location-banner/assumed-location-banner.component';
import { RewardsNavComponent } from '@approot/rewards-nav/rewards-nav.component';
import { PointsBannerComponent } from '@approot/points-banner/points-banner.component';
import * as CheckoutModals from '@features/checkout/modals/modals.index';
import { CheckInService } from '@features/ordering/services/checkin.service';
import { InViewportModule } from 'ng-in-viewport';
import { AddressService  } from '@core/services/address.service';
import { RouterModule, UrlSerializer } from '@angular/router';
import { LowerCaseUrlSerializer } from '@core/serializers/lowerCaseSerializer.serializer';
import { PointsTutorialComponent } from './features/rewards/modals/points-tutorial/points-tutorial.component';
import { RewardService } from './features/rewards/services/reward.service';
import { GeofenceService, PazeService } from './core/services';

declare let gapi: any;

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BrowserAnimationsModule,
    SharedComponentsModule,
    BrowserModule,
    CoreModule,
    NgReduxModule,
    ButtonModule,
    InputTextModule,
    ConfirmDialogModule,
    NgbModule,
    FacebookModule.forRoot(),
    ResponsiveModule.forRoot(),
    HttpClientModule,
    Routing,
    MaterialsModule,
    NgxStripeModule.forRoot(environment.STRIPE_KEY),
    SortablejsModule.forRoot({ animation: 150 }),
    // DeviceDetectorModule.forRoot(),
    NgxMaskModule.forRoot(),
    HammerModule,
    InViewportModule,
    RouterModule,
    StatsigModule
  ],
  declarations: [
    AppComponent,
    AppHeaderComponent,
    AppFooterComponent,
    LandingComponent,
    HomeComponent,
    EmergencyBannerComponent,
    AuthModals.RegisterComponent,
    AuthModals.ForgotPasswordComponent,
    AuthModals.SigninModalComponent,
    AuthModals.SigninIncompleteComponent,
    AuthModals.TemporaryPasswordComponent,
    AuthModals.TermNConditionComponent,
    AuthModals.TncUpdateComponent,
    AuthModals.SigninCodeComponent,
    AuthModals.JitMigrationModalComponent,
    AuthModals.MigrationNudgePromptComponent,
    AuthModals.IdentifierFirstSigninComponent,
    AuthModals.Auth0EmailSignupComponent,
    Auth0CallbackComponent,
    CustomizationModals.NutritionAllergensComponent,
    CustomizationModals.ChooseYourMealComponent,
    ApplyForSupportModalComponent,
    VisitTutorialComponent,
    OrderModals.MyOrderSummaryComponent,
    OrderModals.OrderDetailComponent,
    OrderModals.TimePickerComponent,
    OrderModals.AddFavoriteNameModalComponent,
    OrderModals.FormModalComponent,
    OrderModals.CurbsideNospaceComponent,
    OrderModals.CurbsideSpaceNumberComponent,
    AccountModals.PickuptypeComponent,
    AccountModals.DeleteAccountConfirmationComponent,
    PointsTutorialComponent,
    AccountModals.EditFavoriteComponent,
    CheckoutModals.AddTipComponent,
    CheckoutModals.ConfirmAddressComponent,
    CheckoutModals.SelectPickupComponent,
    SubHeaderComponent,
    RecentOrdersComponent,
    BannerNudgeComponent,
    AssumedLocationBannerComponent,
    PointsBannerComponent,
    RewardsNavComponent
  ],
  providers: [
    HttpClient,
    StartOrderService,
    MealOptionService,
    ChildRecipeService,
    AddFavoriteNameService,
    PickupTimeService,
    EditFavoriteService,
    CustomizationService,
    TaxCalculatorService,
    FinalCheckoutService,
    AddressService,
    ConfirmationService,
    CheckInService,
    RewardService,
    CanDeactivateGuard,
    {
      provide: UrlSerializer,
      useClass: LowerCaseUrlSerializer
    },
    {
      provide: LoggingService,
      useClass: environment.loggingServiceProvider
    },
    {
      provide: ErrorHandler,
      useClass: MonitoringErrorHandler
    },
    {
      provide: 'AppInsightsConfig',
      useValue: environment.appInsightsConfig
    },
    {
      provide: 'LogLevel',
      useValue: environment.defaultLogLevel
    },
    {
      provide: STATSIG_INIT_CONFIG,
      useFactory: () => {
        const statsigConfig = (environment as { statsig?: { clientSdkKey?: string; environmentTier?: string } }).statsig;
        const sdkKey = statsigConfig?.clientSdkKey ?? '';
        const environmentTier = statsigConfig?.environmentTier ?? 'development';
        const persistedState = loadState();
        const persistedUser = persistedState?.user ?? null;
        const user = buildStatsigUser(persistedUser);
        const client = new StatsigClient(sdkKey, user, { environment: { tier: environmentTier } });
        client.initializeSync();
        return { client };
      }
    },
    {
      provide: FeatureFlagService,
      useClass: StatsigFeatureFlagService
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(ngRedux: NgRedux<IAppState>, devTools: DevToolsExtension, private geofence: GeofenceService, private pazeService: PazeService ) {
    gapi.load('client', this.initializeGapi);
    const enhancers = isDevMode() ? [devTools.enhancer()] : [];
    this.initializeRadar();
    const persistedState = loadState();
    this.loadPazeScript();

    // COMMNET OUT THIS CODE AND USE THE UNCOMMENTED OUT CODE AFTER TO USE REDUX TOOLS
    // IF THE REDUX DEV TOOLS FOR CHROME ARE NOT INSTALLED THEN THE SECOND SET OF CODE WILL
    // BREAK THE APPLICATION.
    if (persistedState) {
      ngRedux.configureStore(rootReducer, persistedState);
    } else {
      ngRedux.configureStore(rootReducer, INITIAL_STATE);
    }

    // UN COMMENT OUT THIS CODE IF USING REDUX DEV TOOLS AND WANT TO SEE THE REDUX UPDATES
    /* if (persistedState && isDevMode()) {
      ngRedux.configureStore(rootReducer, persistedState, [], enhancers);
    } else if (isDevMode()) {
      ngRedux.configureStore(rootReducer, INITIAL_STATE, [], enhancers);
    } else {
      ngRedux.configureStore(rootReducer, INITIAL_STATE);
    } */
    ngRedux.subscribe(() => {
      saveState(ngRedux.getState());
    });
  }
  private initializeRadar() {
    this.geofence.initialize();    
  }

  initializeGapi() {
    // Google API initialization
    gapi.client.init({
      'apiKey': environment.GP_API_KEY,
      'clientId': environment.GP_CLIENT_ID,
      'discoveryDocs': ['https://people.googleapis.com/$discovery/rest?version=v1'],
      'scope': environment.GP_SCOPES,
    });
  }
  private loadPazeScript() {
    this.pazeService.loadScript("pazejs").subscribe(
      () => {
        this.pazeService.setPazeLoadStatus(true);      
    },
      () => this.pazeService.setPazeLoadStatus(false)
    );
  }
}
export interface PaymentOptions {
  requestPayerEmail?: boolean;
  requestPayerName?: boolean;
  requestPayerPhone?: boolean;
  requestShipping?: boolean;
  shippingType?: string;
}
